import { capitalize } from '.';
import { SUPPORTED_LANGUAGES } from '../../common/constants';

/**
 * Generates a locale time string based on a passed in Date object
 * @param {Date} date The Date object to extract the time from
 * @returns A time string in the format of HH:MM(A/P)M, where HH is from 00-12. Ex: 06:05PM
 */
export const formatLocaleTime = date => (new Date(date)).toLocaleTimeString('en-us', { hour: '2-digit', minute: '2-digit' }).replace(' ', '');

/**
 * Generates a month-date string based on a passed in Date object
 * @param {Date} date The Date object to extract the date from
 * @param {string} lang The language to format the date in. Checks against the SUPPORTED_LANGUAGES constant
 * @returns A date string in the format of MM/DD, with no leading 0s
 */
export const formatDate = (date, lang) => {
  const wrappedDate = new Date(date);
  if (lang === SUPPORTED_LANGUAGES.FRENCH) {
    return `${wrappedDate.getDate()}/${wrappedDate.getMonth() + 1}`;
  }
  return `${wrappedDate.getMonth() + 1}/${wrappedDate.getDate()}`;
};

/**
 * Returns a string representing the long text equivalent of a Date object's Day property
 * @param {Date} date The Date object to extract the day from
 * @returns A string containing the long text equivalent of the Day property
 */
export const formatDay = date => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][(new Date(date)).getDay()];

/**
 * Generates a 24Hr timestamp string based on the a passed in Date object
 * @param {Date} date The Date object to extract the timestamp from
 * @param {string} lang The language to format the date in. Checks against the SUPPORTED_LANGUAGES constant
 * @returns A timestamp string in the format of HH:MM mm/dd
 */
export const formatTimestamp = (date, lang) => `${formatLocaleTime(date)} ${formatDate(date, lang)}`;

/**
 * Translates the relative time string from the HDate library to the current language
 * @param {string} relativeTime The relative time as calculated by the HDate library (ex. "2 days ago", "4 minutes ago")
 * @param {Function} tFn The translation function from the useTranslation hook
 * @returns The relative time string translated to the current language
 */
export const getTranslationFromHDateRelativeTime = (relativeTime, tFn) => {
  const [count, unit, ago] = relativeTime.split(' ');
  const key = `${unit}${capitalize(ago)}`;
  return tFn(key, { ns: 'dates', count });
};

/**
 * Format string date input to a readable date format for UI components: e.g April 18, 2018
 * @param {string} inputString - Date string to format
 * @param {string} lang - Language to format the date in, backup language is English if not provided
 * @returns {string} - Formatted date string in long format: e.g April 18, 2018
 */
export const toLongLocaleDate = (inputString, lang) => new Date(inputString)
  .toLocaleDateString(lang || SUPPORTED_LANGUAGES.ENGLISH, {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  });

/**
 * Format string date input to a readable date format for UI components: e.g Apr 18 2018
 * @param {string} inputString - Date string to format
 * @param {string} lang - Language to format the date in, backup language is English if not provided
 * @returns {string} - Formatted date string in short format: e.g Apr 18 2018
 */
export const toShortLocaleDate = (inputString, lang) => new Date(inputString)
  .toLocaleDateString(lang || SUPPORTED_LANGUAGES.ENGLISH, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).replace(/[.,]/g, '');
